export const appConfig = {
  app_name: "Histories",
  app_description:
    "A social app for sharing stories, location, and connecting with friends.",
  support_email: "support@thehistories.me",
};

export const privacyPolicy = {
  last_updated: "2026-03-22T00:00:00.000Z",
  content: `# Privacy Policy for Histories

**Last Updated: March 22, 2026**

## 1. Introduction

Welcome to Histories. We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you use our social storytelling application.

## 2. Information We Collect

### 2.1 Information You Provide
- **Account Information**: Username, email address, phone number, and profile picture
- **Content**: Stories, photos, videos, and messages you share
- **Location Data**: When you choose to share your location with friends
- **Communications**: Messages and interactions with other users

### 2.2 Automatically Collected Information
- **Device Information**: Device type, operating system, unique device identifiers
- **Usage Data**: How you interact with our app, features you use, time spent
- **Log Data**: IP address, browser type, pages viewed, crash reports

### 2.3 Location Information
Histories may collect precise location data when you:
- Share your location with friends
- Tag stories with locations
- Enable location-based features

You can control location sharing through your device settings.

## 3. How We Use Your Information

We use your information to:
- Provide and improve our services
- Enable story sharing and social features
- Show your location to friends (when enabled)
- Send notifications about activity
- Ensure safety and prevent abuse
- Comply with legal obligations

## 4. Information Sharing

### 4.1 With Other Users
- Your stories are visible to your friends or selected audience
- Your location is only shared with users you explicitly allow
- Your profile information is visible based on your privacy settings

### 4.2 With Third Parties
We may share information with:
- Service providers who help operate our platform
- Law enforcement when required by law
- Business partners with your consent

We do NOT sell your personal information.

## 5. Your Privacy Controls

### 5.1 Account Settings
- Control who can see your stories
- Manage location sharing preferences
- Block users you don't want to interact with
- Delete your content at any time

### 5.2 Blocking Users
When you block someone on Histories:
- They cannot view your stories or profile
- They cannot send you messages
- They cannot see your location
- Previous conversations are hidden

## 6. Data Retention

- Stories automatically expire after 24 hours unless saved
- Account data is retained while your account is active
- You can request deletion of your account and data

## 7. Security

We implement industry-standard security measures including:
- Encryption of data in transit and at rest
- Regular security audits
- Access controls and authentication

## 8. Children's Privacy

Histories is not intended for users under 13 years of age. We do not knowingly collect information from children under 13.

## 9. Changes to This Policy

We may update this privacy policy periodically. We will notify you of significant changes through the app or via email.

## 10. Contact Us

If you have questions about this privacy policy or our data practices, please contact us at:

**Email**: privacy@thehistories.me
**Address**: Histories Privacy Team

---

By using Histories, you agree to this Privacy Policy.`,
};

export const termsOfService = {
  last_updated: "2026-03-22T00:00:00.000Z",
  content: `# Terms of Service for Histories

**Last Updated: March 22, 2026**

## 1. Acceptance of Terms

By accessing or using Histories, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree, please do not use our services.

## 2. Description of Service

Histories is a social platform that allows users to:
- Share stories, photos, and videos with friends
- Share real-time location with selected contacts
- Connect and communicate with other users
- Discover and engage with content

## 3. Eligibility

To use Histories, you must:
- Be at least 13 years old (or the minimum age in your country)
- Have the legal capacity to enter into this agreement
- Not have been previously banned from our services

## 4. Your Account

### 4.1 Account Creation
- You must provide accurate and complete information
- You are responsible for maintaining account security
- You must not share your login credentials

### 4.2 Account Security
- Notify us immediately of any unauthorized access
- You are responsible for all activity under your account
- We are not liable for losses due to unauthorized use

## 5. User Content

### 5.1 Your Content
You retain ownership of content you create on Histories. By posting content, you grant us a license to:
- Display your content to your chosen audience
- Store and process your content
- Modify content for technical purposes (for example, formatting)

### 5.2 Content Guidelines
You agree NOT to post content that:
- Is illegal, harmful, or threatening
- Harasses or bullies others
- Contains nudity or sexual content
- Infringes intellectual property rights
- Spreads misinformation or spam
- Promotes violence or discrimination

### 5.3 Content Removal
We reserve the right to remove content that violates these terms without notice.

## 6. User Conduct

### 6.1 Prohibited Activities
You agree NOT to:
- Impersonate others or create fake accounts
- Harass, stalk, or threaten other users
- Collect user information without consent
- Use automated systems to access our services
- Attempt to hack or compromise our systems
- Use the service for illegal purposes

### 6.2 Blocking and Reporting
- You can block users who violate your boundaries
- Report inappropriate content or behavior
- We investigate reports and take appropriate action

## 7. Location Sharing

### 7.1 Consent
Location sharing is optional and requires your explicit consent.

### 7.2 Your Control
- You choose who can see your location
- You can stop sharing at any time
- Location history is not permanently stored

### 7.3 Safety
- Only share location with people you trust
- Be aware of your surroundings when sharing
- Report any location-related safety concerns

## 8. Intellectual Property

### 8.1 Our Property
Histories, including its design, features, and content, is protected by intellectual property laws. You may not copy, modify, or distribute our property without permission.

### 8.2 Feedback
Any feedback or suggestions you provide may be used by us without obligation to you.

## 9. Third-Party Services

Histories may contain links to third-party services. We are not responsible for their content or practices.

## 10. Disclaimers

### 10.1 Service Availability
Histories is provided "as is" without warranties. We do not guarantee:
- Uninterrupted or error-free service
- That content will be preserved
- Accuracy of user-generated content

### 10.2 User Interactions
We are not responsible for interactions between users. Exercise caution when meeting people from the app.

## 11. Limitation of Liability

To the maximum extent permitted by law, Histories is not liable for:
- Indirect, incidental, or consequential damages
- Loss of data, profits, or goodwill
- Actions of other users

## 12. Indemnification

You agree to indemnify Histories against claims arising from:
- Your use of the service
- Your content
- Your violation of these terms

## 13. Termination

### 13.1 By You
You may delete your account at any time through the app settings.

### 13.2 By Us
We may suspend or terminate your account for:
- Violation of these terms
- Illegal activity
- Extended inactivity
- At our discretion with notice

## 14. Changes to Terms

We may modify these terms at any time. Continued use after changes constitutes acceptance.

## 15. Governing Law

These terms are governed by the laws of the jurisdiction where Histories operates.

## 16. Dispute Resolution

Any disputes will be resolved through binding arbitration, except where prohibited by law.

## 17. Contact Us

For questions about these terms:

**Email**: legal@thehistories.me
**Address**: Histories Legal Team

---

By using Histories, you acknowledge that you have read, understood, and agree to these Terms of Service.`,
};
